import { useTranslation } from 'react-i18next';
import { SEOHead } from './SEOHead';
import { NavigationLink } from './Router';

export function ManagingSymptomsPage() {
  const { i18n } = useTranslation();
  const isNL = i18n.language === 'nl';
  const isFR = i18n.language === 'fr';

  const content = {
    en: {
      title: "What to Do While Waiting: Managing Food Poisoning Symptoms",
      subtitle: "Essential care tips and when to seek immediate medical help",
      lastUpdated: "Last updated: January 2025",
      disclaimer: "⚠️ IMPORTANT: This guide is for informational purposes only. Always consult a healthcare professional for medical advice. If you have severe symptoms, seek immediate medical attention.",
      intro: "If you suspect you have food poisoning, knowing how to manage your symptoms while waiting for medical care or recovery can make a significant difference. This guide provides essential information on symptom management and crucial warning signs that require immediate medical attention.",
      sections: {
        identifying: {
          title: "Identifying Food Poisoning vs. Other Illnesses",
          intro: "It's important to distinguish food poisoning from other conditions:",
          comparison: [
            {
              condition: "Food Poisoning",
              onset: "2-6 hours after eating (can be up to 72 hours)",
              symptoms: "Nausea, vomiting, diarrhea, stomach cramps, sometimes fever",
              duration: "Usually 1-3 days"
            },
            {
              condition: "Stomach Flu (Gastroenteritis)",
              onset: "1-3 days after exposure to virus",
              symptoms: "Similar to food poisoning but often with body aches",
              duration: "3-7 days"
            },
            {
              condition: "Food Allergy",
              onset: "Minutes to 2 hours after eating",
              symptoms: "Hives, swelling, difficulty breathing, anaphylaxis",
              duration: "Hours (requires immediate treatment)"
            }
          ]
        },
        commonSymptoms: {
          title: "Common Food Poisoning Symptoms",
          mild: {
            title: "Mild to Moderate Symptoms",
            list: [
              "Nausea and loss of appetite",
              "Vomiting (usually stops within 24 hours)",
              "Diarrhea (watery, sometimes with mucus)",
              "Stomach cramps and abdominal pain",
              "Low-grade fever (under 38.5°C/101.3°F)",
              "Headache and general weakness",
              "Muscle aches"
            ]
          },
          severe: {
            title: "Severe Symptoms (Seek Immediate Medical Help)",
            list: [
              "High fever (over 38.5°C/101.3°F)",
              "Blood in vomit or stool",
              "Severe dehydration (dizziness, dry mouth, little/no urination)",
              "Persistent vomiting preventing fluid intake",
              "Diarrhea lasting more than 3 days",
              "Severe abdominal pain or cramping",
              "Signs of shock (rapid heartbeat, confusion)",
              "Neurological symptoms (blurry vision, muscle weakness, tingling)"
            ]
          }
        },
        immediateActions: {
          title: "Immediate Actions to Take",
          steps: [
            {
              title: "1. Stop Eating",
              content: "Give your digestive system a rest. Avoid solid foods until vomiting stops."
            },
            {
              title: "2. Stay Hydrated",
              content: "This is the most important step. Sip small amounts of clear fluids frequently."
            },
            {
              title: "3. Rest",
              content: "Your body needs energy to fight the infection. Avoid strenuous activities."
            },
            {
              title: "4. Monitor Symptoms",
              content: "Keep track of symptom severity and duration. Note any worsening."
            },
            {
              title: "5. Isolate if Possible",
              content: "Some types of food poisoning can be contagious. Practice good hygiene."
            }
          ]
        },
        hydration: {
          title: "Managing Dehydration - The Biggest Risk",
          intro: "Dehydration is the most serious complication of food poisoning. Here's how to prevent and manage it:",
          goodFluids: {
            title: "Recommended Fluids",
            list: [
              "Water (room temperature, small sips)",
              "Oral rehydration solutions (ORS)",
              "Clear broths (vegetable or chicken)",
              "Electrolyte drinks (diluted)",
              "Herbal teas (ginger, peppermint)",
              "Ice chips or popsicles"
            ]
          },
          avoidFluids: {
            title: "Fluids to Avoid",
            list: [
              "Alcohol (increases dehydration)",
              "Caffeine (coffee, tea, sodas)",
              "Milk and dairy products",
              "Fruit juices (can worsen diarrhea)",
              "Very hot or very cold beverages"
            ]
          },
          dehydrationSigns: {
            title: "Signs of Dehydration",
            mild: ["Thirst", "Dry mouth", "Decreased urination", "Dark yellow urine"],
            severe: ["Dizziness or lightheadedness", "Rapid heartbeat", "Sunken eyes", "No tears when crying", "Confusion or irritability"]
          }
        },
        dietProgression: {
          title: "Gradual Return to Eating",
          intro: "Once vomiting stops and you can keep fluids down, gradually reintroduce foods:",
          stages: [
            {
              stage: "Stage 1 (First 24 hours)",
              foods: "Clear liquids only: water, broth, electrolyte drinks"
            },
            {
              stage: "Stage 2 (Day 2)",
              foods: "BRAT diet: Bananas, Rice, Applesauce, Toast. Also: plain crackers, boiled potatoes"
            },
            {
              stage: "Stage 3 (Day 3-4)",
              foods: "Add: lean proteins (chicken, fish), cooked vegetables, eggs"
            },
            {
              stage: "Stage 4 (Day 5+)",
              foods: "Gradually return to normal diet, avoiding spicy, fatty, or dairy foods initially"
            }
          ]
        },
        medications: {
          title: "Medications and Remedies",
          warning: "Always consult a healthcare provider before taking medications",
          options: [
            {
              type: "Anti-diarrheal medications",
              examples: "Loperamide (Imodium)",
              when: "Only for mild cases without fever or blood in stool",
              caution: "Can prolong infection in some cases"
            },
            {
              type: "Anti-nausea medications",
              examples: "Ondansetron, Promethazine",
              when: "For severe nausea preventing fluid intake",
              caution: "Requires prescription"
            },
            {
              type: "Pain relievers",
              examples: "Acetaminophen (Paracetamol)",
              when: "For fever and body aches",
              caution: "Avoid NSAIDs like ibuprofen which can irritate stomach"
            },
            {
              type: "Probiotics",
              examples: "Lactobacillus, Saccharomyces",
              when: "May help restore gut bacteria",
              caution: "Evidence is mixed; consult doctor"
            }
          ]
        },
        homeRemedies: {
          title: "Natural Remedies and Comfort Measures",
          remedies: [
            {
              remedy: "Ginger",
              benefit: "Reduces nausea and vomiting",
              how: "Ginger tea, crystallized ginger, or ginger ale (flat)"
            },
            {
              remedy: "Peppermint",
              benefit: "Soothes stomach and reduces cramping",
              how: "Peppermint tea or peppermint oil capsules"
            },
            {
              remedy: "Heat Application",
              benefit: "Relieves abdominal cramps",
              how: "Heating pad or hot water bottle on stomach"
            },
            {
              remedy: "BRAT Diet",
              benefit: "Easy to digest, helps firm stools",
              how: "Bananas, rice, applesauce, toast"
            },
            {
              remedy: "Chamomile Tea",
              benefit: "Anti-inflammatory, calming",
              how: "Steep chamomile tea, drink warm"
            }
          ]
        },
        whenToSeek: {
          title: "When to Seek Medical Help",
          immediate: {
            title: "Seek Emergency Care Immediately If:",
            signs: [
              "Blood in vomit or stool",
              "High fever (over 38.5°C/101.3°F)",
              "Signs of severe dehydration",
              "Severe abdominal pain",
              "Confusion or disorientation",
              "Rapid heart rate or breathing",
              "Symptoms of shock"
            ]
          },
          doctor: {
            title: "See a Doctor Within 24 Hours If:",
            signs: [
              "Symptoms persist beyond 3 days",
              "Unable to keep fluids down for 24 hours",
              "Moderate dehydration",
              "Underlying health conditions (diabetes, immune disorders)",
              "Pregnancy",
              "Age over 65 or under 5"
            ]
          }
        },
        prevention: {
          title: "Preventing Spread to Others",
          measures: [
            "Wash hands frequently with soap and water",
            "Avoid preparing food for others while ill",
            "Disinfect bathroom surfaces regularly",
            "Wash contaminated clothing and bedding in hot water",
            "Stay home from work/school until 48 hours after symptoms stop",
            "Don't share towels, utensils, or personal items"
          ]
        }
      },
      finalNote: "Remember: Most food poisoning cases resolve on their own within a few days. However, don't hesitate to seek medical help if you're concerned about your symptoms or if they worsen.",
      cta: {
        report: "Report This Restaurant",
        guide: "How to Report Food Poisoning",
        emergency: "Find Emergency Services"
      }
    },
    nl: {
      title: "Wat te Doen Tijdens het Wachten: Symptomen van Voedselvergiftiging Beheren",
      subtitle: "Essentiële zorgtips en wanneer onmiddellijk medische hulp te zoeken",
      lastUpdated: "Laatst bijgewerkt: januari 2025",
      disclaimer: "⚠️ BELANGRIJK: Deze gids is alleen voor informatieve doeleinden. Raadpleeg altijd een zorgverlener voor medisch advies. Bij ernstige symptomen, zoek onmiddellijk medische hulp.",
      intro: "Als u vermoedt dat u voedselvergiftiging heeft, kan het weten hoe u uw symptomen kunt beheren tijdens het wachten op medische zorg of herstel een significant verschil maken. Deze gids biedt essentiële informatie over symptoombeheersing en cruciale waarschuwingssignalen die onmiddellijke medische aandacht vereisen.",
      sections: {
        identifying: {
          title: "Voedselvergiftiging Identificeren vs. Andere Ziekten",
          intro: "Het is belangrijk om voedselvergiftiging te onderscheiden van andere aandoeningen:",
          comparison: [
            {
              condition: "Voedselvergiftiging",
              onset: "2-6 uur na het eten (kan tot 72 uur zijn)",
              symptoms: "Misselijkheid, braken, diarree, maagkrampen, soms koorts",
              duration: "Meestal 1-3 dagen"
            },
            {
              condition: "Buikgriep (Gastro-enteritis)",
              onset: "1-3 dagen na blootstelling aan virus",
              symptoms: "Vergelijkbaar met voedselvergiftiging maar vaak met spierpijn",
              duration: "3-7 dagen"
            },
            {
              condition: "Voedselallergie",
              onset: "Minuten tot 2 uur na het eten",
              symptoms: "Netelroos, zwelling, ademhalingsproblemen, anafylaxie",
              duration: "Uren (vereist onmiddellijke behandeling)"
            }
          ]
        },
        commonSymptoms: {
          title: "Veel Voorkomende Symptomen van Voedselvergiftiging",
          mild: {
            title: "Milde tot Matige Symptomen",
            list: [
              "Misselijkheid en verlies van eetlust",
              "Braken (stopt meestal binnen 24 uur)",
              "Diarree (waterig, soms met slijm)",
              "Maagkrampen en buikpijn",
              "Lage koorts (onder 38,5°C)",
              "Hoofdpijn en algemene zwakte",
              "Spierpijn"
            ]
          },
          severe: {
            title: "Ernstige Symptomen (Zoek Onmiddellijk Medische Hulp)",
            list: [
              "Hoge koorts (boven 38,5°C)",
              "Bloed in braaksel of ontlasting",
              "Ernstige uitdroging (duizeligheid, droge mond, weinig/geen urine)",
              "Aanhoudend braken dat vochtinname verhindert",
              "Diarree die langer dan 3 dagen duurt",
              "Ernstige buikpijn of krampen",
              "Tekenen van shock (snelle hartslag, verwarring)",
              "Neurologische symptomen (wazig zien, spierzwakte, tintelingen)"
            ]
          }
        },
        immediateActions: {
          title: "Onmiddellijke Acties om te Ondernemen",
          steps: [
            {
              title: "1. Stop met Eten",
              content: "Geef uw spijsverteringssysteem rust. Vermijd vast voedsel totdat het braken stopt."
            },
            {
              title: "2. Blijf Gehydrateerd",
              content: "Dit is de belangrijkste stap. Neem regelmatig kleine slokjes heldere vloeistoffen."
            },
            {
              title: "3. Rust",
              content: "Uw lichaam heeft energie nodig om de infectie te bestrijden. Vermijd inspannende activiteiten."
            },
            {
              title: "4. Monitor Symptomen",
              content: "Houd de ernst en duur van symptomen bij. Let op verslechtering."
            },
            {
              title: "5. Isoleer indien Mogelijk",
              content: "Sommige soorten voedselvergiftiging kunnen besmettelijk zijn. Pas goede hygiëne toe."
            }
          ]
        },
        hydration: {
          title: "Uitdroging Beheren - Het Grootste Risico",
          intro: "Uitdroging is de ernstigste complicatie van voedselvergiftiging. Zo voorkomt en beheert u het:",
          goodFluids: {
            title: "Aanbevolen Vloeistoffen",
            list: [
              "Water (kamertemperatuur, kleine slokjes)",
              "Orale rehydratatie oplossingen (ORS)",
              "Heldere bouillon (groente of kip)",
              "Elektrolyt dranken (verdund)",
              "Kruidenthee (gember, pepermunt)",
              "IJsblokjes of ijslollies"
            ]
          },
          avoidFluids: {
            title: "Te Vermijden Vloeistoffen",
            list: [
              "Alcohol (verhoogt uitdroging)",
              "Cafeïne (koffie, thee, frisdrank)",
              "Melk en zuivelproducten",
              "Vruchtensappen (kan diarree verergeren)",
              "Zeer warme of zeer koude dranken"
            ]
          },
          dehydrationSigns: {
            title: "Tekenen van Uitdroging",
            mild: ["Dorst", "Droge mond", "Verminderde urine", "Donkergele urine"],
            severe: ["Duizeligheid", "Snelle hartslag", "Ingevallen ogen", "Geen tranen bij huilen", "Verwarring of prikkelbaarheid"]
          }
        },
        dietProgression: {
          title: "Geleidelijke Terugkeer naar Eten",
          intro: "Zodra het braken stopt en u vloeistoffen kunt binnenhouden, introduceer geleidelijk voedsel:",
          stages: [
            {
              stage: "Fase 1 (Eerste 24 uur)",
              foods: "Alleen heldere vloeistoffen: water, bouillon, elektrolyt dranken"
            },
            {
              stage: "Fase 2 (Dag 2)",
              foods: "BRAT dieet: Bananen, Rijst, Appelmoes, Toast. Ook: gewone crackers, gekookte aardappelen"
            },
            {
              stage: "Fase 3 (Dag 3-4)",
              foods: "Voeg toe: magere eiwitten (kip, vis), gekookte groenten, eieren"
            },
            {
              stage: "Fase 4 (Dag 5+)",
              foods: "Geleidelijk terug naar normaal dieet, vermijd aanvankelijk pittig, vet of zuivel"
            }
          ]
        },
        medications: {
          title: "Medicijnen en Remedies",
          warning: "Raadpleeg altijd een zorgverlener voordat u medicijnen inneemt",
          options: [
            {
              type: "Anti-diarree medicijnen",
              examples: "Loperamide (Imodium)",
              when: "Alleen voor milde gevallen zonder koorts of bloed in ontlasting",
              caution: "Kan infectie in sommige gevallen verlengen"
            },
            {
              type: "Anti-misselijkheid medicijnen",
              examples: "Ondansetron, Promethazine",
              when: "Voor ernstige misselijkheid die vochtinname verhindert",
              caution: "Vereist recept"
            },
            {
              type: "Pijnstillers",
              examples: "Acetaminofen (Paracetamol)",
              when: "Voor koorts en spierpijn",
              caution: "Vermijd NSAID's zoals ibuprofen die de maag kunnen irriteren"
            },
            {
              type: "Probiotica",
              examples: "Lactobacillus, Saccharomyces",
              when: "Kan helpen darmbacteriën te herstellen",
              caution: "Bewijs is gemengd; raadpleeg arts"
            }
          ]
        },
        homeRemedies: {
          title: "Natuurlijke Remedies en Comfortmaatregelen",
          remedies: [
            {
              remedy: "Gember",
              benefit: "Vermindert misselijkheid en braken",
              how: "Gemberthee, gekristalliseerde gember, of ginger ale (plat)"
            },
            {
              remedy: "Pepermunt",
              benefit: "Kalmeert de maag en vermindert krampen",
              how: "Pepermuntthee of pepermuntolie capsules"
            },
            {
              remedy: "Warmte Toepassing",
              benefit: "Verlicht buikkrampen",
              how: "Verwarmingskussen of warmwaterkruik op de buik"
            },
            {
              remedy: "BRAT Dieet",
              benefit: "Gemakkelijk te verteren, helpt ontlasting steviger te maken",
              how: "Bananen, rijst, appelmoes, toast"
            },
            {
              remedy: "Kamillethee",
              benefit: "Ontstekingsremmend, kalmerend",
              how: "Trek kamillethee, drink warm"
            }
          ]
        },
        whenToSeek: {
          title: "Wanneer Medische Hulp te Zoeken",
          immediate: {
            title: "Zoek Onmiddellijk Spoedhulp Als:",
            signs: [
              "Bloed in braaksel of ontlasting",
              "Hoge koorts (boven 38,5°C)",
              "Tekenen van ernstige uitdroging",
              "Ernstige buikpijn",
              "Verwarring of desoriëntatie",
              "Snelle hartslag of ademhaling",
              "Symptomen van shock"
            ]
          },
          doctor: {
            title: "Zie een Arts Binnen 24 Uur Als:",
            signs: [
              "Symptomen blijven langer dan 3 dagen aanhouden",
              "Onmogelijk om vloeistoffen binnen te houden voor 24 uur",
              "Matige uitdroging",
              "Onderliggende gezondheidsproblemen (diabetes, immuunstoornissen)",
              "Zwangerschap",
              "Leeftijd boven 65 of onder 5"
            ]
          }
        },
        prevention: {
          title: "Verspreiding naar Anderen Voorkomen",
          measures: [
            "Was regelmatig handen met zeep en water",
            "Vermijd het bereiden van voedsel voor anderen tijdens ziekte",
            "Desinfecteer badkameroppervlakken regelmatig",
            "Was besmette kleding en beddengoed in heet water",
            "Blijf thuis van werk/school tot 48 uur na het stoppen van symptomen",
            "Deel geen handdoeken, bestek of persoonlijke items"
          ]
        }
      },
      finalNote: "Onthoud: De meeste gevallen van voedselvergiftiging verdwijnen vanzelf binnen een paar dagen. Aarzel echter niet om medische hulp te zoeken als u zich zorgen maakt over uw symptomen of als ze verergeren.",
      cta: {
        report: "Meld Dit Restaurant",
        guide: "Hoe Voedselvergiftiging te Melden",
        emergency: "Vind Nooddiensten"
      }
    },
    fr: {
      title: "Que Faire en Attendant: Gérer les Symptômes d'Intoxication Alimentaire",
      subtitle: "Conseils de soins essentiels et quand chercher une aide médicale immédiate",
      lastUpdated: "Dernière mise à jour: janvier 2025",
      disclaimer: "⚠️ IMPORTANT: Ce guide est à titre informatif uniquement. Consultez toujours un professionnel de la santé pour des conseils médicaux. Si vous avez des symptômes graves, consultez immédiatement un médecin.",
      intro: "Si vous soupçonnez avoir une intoxication alimentaire, savoir comment gérer vos symptômes en attendant des soins médicaux ou la guérison peut faire une différence significative. Ce guide fournit des informations essentielles sur la gestion des symptômes et les signes d'alerte cruciaux qui nécessitent une attention médicale immédiate.",
      sections: {
        identifying: {
          title: "Identifier l'Intoxication Alimentaire vs. Autres Maladies",
          intro: "Il est important de distinguer l'intoxication alimentaire d'autres conditions:",
          comparison: [
            {
              condition: "Intoxication Alimentaire",
              onset: "2-6 heures après avoir mangé (peut aller jusqu'à 72 heures)",
              symptoms: "Nausées, vomissements, diarrhée, crampes d'estomac, parfois fièvre",
              duration: "Généralement 1-3 jours"
            },
            {
              condition: "Grippe Intestinale (Gastro-entérite)",
              onset: "1-3 jours après exposition au virus",
              symptoms: "Similaire à l'intoxication alimentaire mais souvent avec des courbatures",
              duration: "3-7 jours"
            },
            {
              condition: "Allergie Alimentaire",
              onset: "Minutes à 2 heures après avoir mangé",
              symptoms: "Urticaire, gonflement, difficulté respiratoire, anaphylaxie",
              duration: "Heures (nécessite un traitement immédiat)"
            }
          ]
        },
        commonSymptoms: {
          title: "Symptômes Courants d'Intoxication Alimentaire",
          mild: {
            title: "Symptômes Légers à Modérés",
            list: [
              "Nausées et perte d'appétit",
              "Vomissements (s'arrêtent généralement dans les 24 heures)",
              "Diarrhée (aqueuse, parfois avec du mucus)",
              "Crampes d'estomac et douleurs abdominales",
              "Fièvre légère (moins de 38,5°C)",
              "Maux de tête et faiblesse générale",
              "Douleurs musculaires"
            ]
          },
          severe: {
            title: "Symptômes Graves (Cherchez une Aide Médicale Immédiate)",
            list: [
              "Forte fièvre (plus de 38,5°C)",
              "Sang dans les vomissements ou les selles",
              "Déshydratation sévère (vertiges, bouche sèche, peu/pas d'urine)",
              "Vomissements persistants empêchant l'absorption de liquides",
              "Diarrhée durant plus de 3 jours",
              "Douleurs abdominales ou crampes sévères",
              "Signes de choc (rythme cardiaque rapide, confusion)",
              "Symptômes neurologiques (vision floue, faiblesse musculaire, picotements)"
            ]
          }
        },
        immediateActions: {
          title: "Actions Immédiates à Prendre",
          steps: [
            {
              title: "1. Arrêtez de Manger",
              content: "Donnez du repos à votre système digestif. Évitez les aliments solides jusqu'à l'arrêt des vomissements."
            },
            {
              title: "2. Restez Hydraté",
              content: "C'est l'étape la plus importante. Buvez fréquemment de petites gorgées de liquides clairs."
            },
            {
              title: "3. Reposez-vous",
              content: "Votre corps a besoin d'énergie pour combattre l'infection. Évitez les activités intenses."
            },
            {
              title: "4. Surveillez les Symptômes",
              content: "Gardez une trace de la gravité et de la durée des symptômes. Notez toute aggravation."
            },
            {
              title: "5. Isolez-vous si Possible",
              content: "Certains types d'intoxication alimentaire peuvent être contagieux. Pratiquez une bonne hygiène."
            }
          ]
        },
        hydration: {
          title: "Gérer la Déshydratation - Le Plus Grand Risque",
          intro: "La déshydratation est la complication la plus grave de l'intoxication alimentaire. Voici comment la prévenir et la gérer:",
          goodFluids: {
            title: "Liquides Recommandés",
            list: [
              "Eau (température ambiante, petites gorgées)",
              "Solutions de réhydratation orale (SRO)",
              "Bouillons clairs (légumes ou poulet)",
              "Boissons électrolytiques (diluées)",
              "Tisanes (gingembre, menthe poivrée)",
              "Glaçons ou glaces à l'eau"
            ]
          },
          avoidFluids: {
            title: "Liquides à Éviter",
            list: [
              "Alcool (augmente la déshydratation)",
              "Caféine (café, thé, sodas)",
              "Lait et produits laitiers",
              "Jus de fruits (peut aggraver la diarrhée)",
              "Boissons très chaudes ou très froides"
            ]
          },
          dehydrationSigns: {
            title: "Signes de Déshydratation",
            mild: ["Soif", "Bouche sèche", "Diminution de la miction", "Urine jaune foncé"],
            severe: ["Vertiges", "Rythme cardiaque rapide", "Yeux enfoncés", "Pas de larmes en pleurant", "Confusion ou irritabilité"]
          }
        },
        dietProgression: {
          title: "Retour Progressif à l'Alimentation",
          intro: "Une fois les vomissements arrêtés et que vous pouvez garder les liquides, réintroduisez progressivement les aliments:",
          stages: [
            {
              stage: "Étape 1 (Premières 24 heures)",
              foods: "Liquides clairs uniquement: eau, bouillon, boissons électrolytiques"
            },
            {
              stage: "Étape 2 (Jour 2)",
              foods: "Régime BRAT: Bananes, Riz, Compote de pommes, Pain grillé. Aussi: crackers nature, pommes de terre bouillies"
            },
            {
              stage: "Étape 3 (Jour 3-4)",
              foods: "Ajouter: protéines maigres (poulet, poisson), légumes cuits, œufs"
            },
            {
              stage: "Étape 4 (Jour 5+)",
              foods: "Retour progressif au régime normal, éviter initialement les aliments épicés, gras ou laitiers"
            }
          ]
        },
        medications: {
          title: "Médicaments et Remèdes",
          warning: "Consultez toujours un professionnel de la santé avant de prendre des médicaments",
          options: [
            {
              type: "Médicaments anti-diarrhéiques",
              examples: "Lopéramide (Imodium)",
              when: "Seulement pour les cas légers sans fièvre ni sang dans les selles",
              caution: "Peut prolonger l'infection dans certains cas"
            },
            {
              type: "Médicaments anti-nausée",
              examples: "Ondansétron, Prométhazine",
              when: "Pour les nausées sévères empêchant l'absorption de liquides",
              caution: "Nécessite une ordonnance"
            },
            {
              type: "Analgésiques",
              examples: "Acétaminophène (Paracétamol)",
              when: "Pour la fièvre et les courbatures",
              caution: "Évitez les AINS comme l'ibuprofène qui peuvent irriter l'estomac"
            },
            {
              type: "Probiotiques",
              examples: "Lactobacillus, Saccharomyces",
              when: "Peut aider à restaurer les bactéries intestinales",
              caution: "Les preuves sont mitigées; consultez un médecin"
            }
          ]
        },
        homeRemedies: {
          title: "Remèdes Naturels et Mesures de Confort",
          remedies: [
            {
              remedy: "Gingembre",
              benefit: "Réduit les nausées et les vomissements",
              how: "Thé au gingembre, gingembre cristallisé, ou soda au gingembre (plat)"
            },
            {
              remedy: "Menthe Poivrée",
              benefit: "Apaise l'estomac et réduit les crampes",
              how: "Thé à la menthe poivrée ou capsules d'huile de menthe poivrée"
            },
            {
              remedy: "Application de Chaleur",
              benefit: "Soulage les crampes abdominales",
              how: "Coussin chauffant ou bouillotte sur l'estomac"
            },
            {
              remedy: "Régime BRAT",
              benefit: "Facile à digérer, aide à raffermir les selles",
              how: "Bananes, riz, compote de pommes, pain grillé"
            },
            {
              remedy: "Thé à la Camomille",
              benefit: "Anti-inflammatoire, apaisant",
              how: "Infuser du thé à la camomille, boire chaud"
            }
          ]
        },
        whenToSeek: {
          title: "Quand Chercher une Aide Médicale",
          immediate: {
            title: "Cherchez des Soins d'Urgence Immédiatement Si:",
            signs: [
              "Sang dans les vomissements ou les selles",
              "Forte fièvre (plus de 38,5°C)",
              "Signes de déshydratation sévère",
              "Douleurs abdominales sévères",
              "Confusion ou désorientation",
              "Rythme cardiaque ou respiration rapide",
              "Symptômes de choc"
            ]
          },
          doctor: {
            title: "Consultez un Médecin Dans les 24 Heures Si:",
            signs: [
              "Les symptômes persistent au-delà de 3 jours",
              "Incapacité de garder les liquides pendant 24 heures",
              "Déshydratation modérée",
              "Conditions de santé sous-jacentes (diabète, troubles immunitaires)",
              "Grossesse",
              "Âge supérieur à 65 ans ou inférieur à 5 ans"
            ]
          }
        },
        prevention: {
          title: "Prévenir la Propagation aux Autres",
          measures: [
            "Lavez-vous les mains fréquemment avec du savon et de l'eau",
            "Évitez de préparer de la nourriture pour d'autres pendant la maladie",
            "Désinfectez régulièrement les surfaces de la salle de bain",
            "Lavez les vêtements et la literie contaminés à l'eau chaude",
            "Restez à la maison du travail/école jusqu'à 48 heures après l'arrêt des symptômes",
            "Ne partagez pas serviettes, ustensiles ou objets personnels"
          ]
        }
      },
      finalNote: "Rappelez-vous: La plupart des cas d'intoxication alimentaire se résolvent d'eux-mêmes en quelques jours. Cependant, n'hésitez pas à chercher une aide médicale si vous êtes préoccupé par vos symptômes ou s'ils s'aggravent.",
      cta: {
        report: "Signaler Ce Restaurant",
        guide: "Comment Signaler une Intoxication Alimentaire",
        emergency: "Trouver les Services d'Urgence"
      }
    }
  };

  const lang = isFR ? 'fr' : (isNL ? 'nl' : 'en');
  const t = content[lang];

  return (
    <>
      <SEOHead page="managing-symptoms" />
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 py-8 md:px-8">
          {/* Emergency Banner */}
          <div className="mb-8 p-4 bg-red-600 text-white rounded-lg">
            <p className="text-center font-bold">
              {lang === 'fr' ? '🚨 Urgence Médicale?' : (lang === 'nl' ? '🚨 Medisch Noodgeval?' : '🚨 Medical Emergency?')} 
              <span className="ml-2">
                {lang === 'fr' ? 'Appelez le 112' : (lang === 'nl' ? 'Bel 112' : 'Call 112')}
              </span>
            </p>
          </div>

          {/* Header */}
          <header className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {t.title}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-2">
              {t.subtitle}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t.lastUpdated}
            </p>
          </header>

          {/* Disclaimer */}
          <div className="mb-8 p-6 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border-2 border-yellow-400">
            <p className="text-gray-700 dark:text-gray-300 font-medium">
              {t.disclaimer}
            </p>
          </div>

          {/* Introduction */}
          <div className="mb-12 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-lg text-gray-700 dark:text-gray-300">
              {t.intro}
            </p>
          </div>

          {/* Main Content */}
          <article className="space-y-12">
            {/* Identifying Section */}
            <section>
              <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
                {t.sections.identifying.title}
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-6">{t.sections.identifying.intro}</p>
              <div className="overflow-x-auto">
                <table className="w-full bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left">{lang === 'fr' ? 'Condition' : (lang === 'nl' ? 'Aandoening' : 'Condition')}</th>
                      <th className="px-4 py-3 text-left">{lang === 'fr' ? 'Début' : (lang === 'nl' ? 'Begin' : 'Onset')}</th>
                      <th className="px-4 py-3 text-left">{lang === 'fr' ? 'Symptômes' : (lang === 'nl' ? 'Symptomen' : 'Symptoms')}</th>
                      <th className="px-4 py-3 text-left">{lang === 'fr' ? 'Durée' : (lang === 'nl' ? 'Duur' : 'Duration')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {t.sections.identifying.comparison.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 font-medium">{item.condition}</td>
                        <td className="px-4 py-3 text-sm">{item.onset}</td>
                        <td className="px-4 py-3 text-sm">{item.symptoms}</td>
                        <td className="px-4 py-3 text-sm">{item.duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Common Symptoms */}
            <section>
              <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
                {t.sections.commonSymptoms.title}
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-4 text-green-800 dark:text-green-200">
                    {t.sections.commonSymptoms.mild.title}
                  </h3>
                  <ul className="space-y-2">
                    {t.sections.commonSymptoms.mild.list.map((symptom, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-green-600 dark:text-green-400 mr-2">•</span>
                        <span className="text-gray-700 dark:text-gray-300">{symptom}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-4 text-red-800 dark:text-red-200">
                    {t.sections.commonSymptoms.severe.title}
                  </h3>
                  <ul className="space-y-2">
                    {t.sections.commonSymptoms.severe.list.map((symptom, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-red-600 dark:text-red-400 mr-2">⚠️</span>
                        <span className="text-gray-700 dark:text-gray-300">{symptom}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Immediate Actions */}
            <section>
              <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
                {t.sections.immediateActions.title}
              </h2>
              <div className="space-y-4">
                {t.sections.immediateActions.steps.map((step, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                      {step.title}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300">{step.content}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Hydration Management */}
            <section>
              <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
                {t.sections.hydration.title}
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-6">{t.sections.hydration.intro}</p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4 text-blue-800 dark:text-blue-200">
                    ✅ {t.sections.hydration.goodFluids.title}
                  </h3>
                  <ul className="space-y-2">
                    {t.sections.hydration.goodFluids.list.map((fluid, index) => (
                      <li key={index} className="text-gray-700 dark:text-gray-300">• {fluid}</li>
                    ))}
                  </ul>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4 text-orange-800 dark:text-orange-200">
                    ❌ {t.sections.hydration.avoidFluids.title}
                  </h3>
                  <ul className="space-y-2">
                    {t.sections.hydration.avoidFluids.list.map((fluid, index) => (
                      <li key={index} className="text-gray-700 dark:text-gray-300">• {fluid}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                  💧 {t.sections.hydration.dehydrationSigns.title}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-2 text-gray-800 dark:text-gray-200">
                      {lang === 'fr' ? 'Légère' : (lang === 'nl' ? 'Mild' : 'Mild')}
                    </h4>
                    <ul className="space-y-1">
                      {t.sections.hydration.dehydrationSigns.mild.map((sign, index) => (
                        <li key={index} className="text-gray-700 dark:text-gray-300 text-sm">• {sign}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2 text-gray-800 dark:text-gray-200">
                      {lang === 'fr' ? 'Sévère' : (lang === 'nl' ? 'Ernstig' : 'Severe')}
                    </h4>
                    <ul className="space-y-1">
                      {t.sections.hydration.dehydrationSigns.severe.map((sign, index) => (
                        <li key={index} className="text-gray-700 dark:text-gray-300 text-sm">• {sign}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Diet Progression */}
            <section>
              <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
                {t.sections.dietProgression.title}
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-6">{t.sections.dietProgression.intro}</p>
              <div className="space-y-4">
                {t.sections.dietProgression.stages.map((stage, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div className="flex-grow bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{stage.stage}</h3>
                      <p className="text-gray-700 dark:text-gray-300">{stage.foods}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Medications */}
            <section>
              <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
                {t.sections.medications.title}
              </h2>
              <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-400">
                <p className="text-gray-700 dark:text-gray-300 font-medium">
                  ⚠️ {t.sections.medications.warning}
                </p>
              </div>
              <div className="space-y-4">
                {t.sections.medications.options.map((med, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                      {med.type}
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p className="text-gray-700 dark:text-gray-300">
                        <span className="font-medium">{lang === 'fr' ? 'Exemples:' : (lang === 'nl' ? 'Voorbeelden:' : 'Examples:')}</span> {med.examples}
                      </p>
                      <p className="text-gray-700 dark:text-gray-300">
                        <span className="font-medium">{lang === 'fr' ? 'Quand:' : (lang === 'nl' ? 'Wanneer:' : 'When:')}</span> {med.when}
                      </p>
                      <p className="text-yellow-700 dark:text-yellow-300">
                        <span className="font-medium">{lang === 'fr' ? 'Attention:' : (lang === 'nl' ? 'Let op:' : 'Caution:')}</span> {med.caution}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Home Remedies */}
            <section>
              <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
                {t.sections.homeRemedies.title}
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {t.sections.homeRemedies.remedies.map((remedy, index) => (
                  <div key={index} className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <h3 className="font-semibold text-purple-800 dark:text-purple-200 mb-2">
                      🌿 {remedy.remedy}
                    </h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                      <span className="font-medium">{lang === 'fr' ? 'Bénéfice:' : (lang === 'nl' ? 'Voordeel:' : 'Benefit:')}</span> {remedy.benefit}
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">{lang === 'fr' ? 'Comment:' : (lang === 'nl' ? 'Hoe:' : 'How:')}</span> {remedy.how}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* When to Seek Help */}
            <section>
              <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
                {t.sections.whenToSeek.title}
              </h2>
              <div className="space-y-6">
                <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-lg border-2 border-red-400">
                  <h3 className="text-xl font-semibold mb-4 text-red-800 dark:text-red-200">
                    🚨 {t.sections.whenToSeek.immediate.title}
                  </h3>
                  <ul className="space-y-2">
                    {t.sections.whenToSeek.immediate.signs.map((sign, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-red-600 dark:text-red-400 mr-2">•</span>
                        <span className="text-gray-700 dark:text-gray-300">{sign}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-4 text-orange-800 dark:text-orange-200">
                    ⚕️ {t.sections.whenToSeek.doctor.title}
                  </h3>
                  <ul className="space-y-2">
                    {t.sections.whenToSeek.doctor.signs.map((sign, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-orange-600 dark:text-orange-400 mr-2">•</span>
                        <span className="text-gray-700 dark:text-gray-300">{sign}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Prevention */}
            <section>
              <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
                {t.sections.prevention.title}
              </h2>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg">
                <ul className="space-y-3">
                  {t.sections.prevention.measures.map((measure, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-blue-600 dark:text-blue-400 mr-3">🛡️</span>
                      <span className="text-gray-700 dark:text-gray-300">{measure}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </article>

          {/* Final Note */}
          <div className="mt-12 p-6 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <p className="text-gray-700 dark:text-gray-300">
              💚 {t.finalNote}
            </p>
          </div>

          {/* Call to Action */}
          <div className="mt-12 text-center space-y-4">
            <NavigationLink
              to="/report-food-poisoning"
              className="inline-block px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
            >
              {t.cta.report}
            </NavigationLink>
            <div className="space-x-4">
              <NavigationLink
                to="/ultimate-guide"
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
              >
                {t.cta.guide}
              </NavigationLink>
              <a
                href="tel:112"
                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 underline font-semibold"
              >
                {t.cta.emergency}
              </a>
            </div>
            <div className="mt-4">
              <NavigationLink
                to="/"
                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
              >
                {lang === 'fr' ? "Verifier le score d'hygiene de votre restaurant" : (lang === 'nl' ? 'Controleer de hygienescore van uw restaurant' : "Check your restaurant's hygiene score")} &rarr;
              </NavigationLink>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}